import type { StreamInfo } from '../ws/types.ts';
import type { MediaEngineHandle } from '../workers/media-engine-proxy.ts';
import type { StreamDecoderMetrics } from '../workers/media-engine.worker.ts';
import { createTile, type Tile } from './tile.ts';
import { createMilkdropOverlay, type MilkdropOverlay } from './milkdrop.ts';
import { diagLogsEnabled } from '../diag-logs.ts';

interface FramePacingMetrics {
  avgDeltaMs: number;
  stdDevMs: number;
  minDeltaMs: number;
  maxDeltaMs: number;
}

const SHOW_ID_KEY = 'stream-activity-show-id';
let showIdCache: Record<string, boolean> | null = null;

function loadShowIdPrefs(): Record<string, boolean> {
  if (showIdCache) return showIdCache;
  try {
    const raw = localStorage.getItem(SHOW_ID_KEY);
    showIdCache = raw ? JSON.parse(raw) : {};
  } catch {
    showIdCache = {};
  }
  return showIdCache ?? {};
}

function saveShowIdPrefs() {
  try {
    localStorage.setItem(SHOW_ID_KEY, JSON.stringify(showIdCache ?? {}));
  } catch {}
}

function getShowIdPref(prefId: string): boolean | undefined {
  return loadShowIdPrefs()[prefId];
}

function setShowIdPref(prefId: string, show: boolean) {
  const prefs = loadShowIdPrefs();
  prefs[prefId] = show;
  saveShowIdPrefs();
}

function purgeShowIdPrefs(activeIds: Set<string>) {
  const prefs = loadShowIdPrefs();
  let changed = false;
  for (const id of Object.keys(prefs)) {
    if (!activeIds.has(id)) {
      delete prefs[id];
      changed = true;
    }
  }
  if (changed) saveShowIdPrefs();
}

const WATCHING_KEY = 'stream-activity-watching';
let watchingCache: Record<string, boolean> | null = null;

function loadWatchingPrefs(): Record<string, boolean> {
  if (watchingCache) return watchingCache;
  try {
    const raw = localStorage.getItem(WATCHING_KEY);
    watchingCache = raw ? JSON.parse(raw) : {};
  } catch {
    watchingCache = {};
  }
  return watchingCache ?? {};
}

function saveWatchingPrefs() {
  try {
    localStorage.setItem(WATCHING_KEY, JSON.stringify(watchingCache ?? {}));
  } catch {}
}

function getWatchingPref(prefId: string): boolean {
  const val = loadWatchingPrefs()[prefId];
  return val === undefined ? true : !!val;
}

function setWatchingPref(prefId: string, watching: boolean) {
  const prefs = loadWatchingPrefs();
  if (watching) {
    prefs[prefId] = true;
  } else {
    delete prefs[prefId];
  }
  saveWatchingPrefs();
}

function purgeWatchingPrefs(activeIds: Set<string>) {
  const prefs = loadWatchingPrefs();
  let changed = false;
  for (const id of Object.keys(prefs)) {
    if (!activeIds.has(id)) {
      delete prefs[id];
      changed = true;
    }
  }
  if (changed) saveWatchingPrefs();
}

const LATENCY_MODE_KEY = 'stream-activity-latency-mode';
let latencyModeCache: Record<string, 'low' | 'smooth'> | null = null;

function loadLatencyModePrefs(): Record<string, 'low' | 'smooth'> {
  if (latencyModeCache) return latencyModeCache;
  try {
    const raw = localStorage.getItem(LATENCY_MODE_KEY);
    latencyModeCache = raw ? JSON.parse(raw) : {};
  } catch {
    latencyModeCache = {};
  }
  return latencyModeCache ?? {};
}

function saveLatencyModePrefs() {
  try {
    localStorage.setItem(LATENCY_MODE_KEY, JSON.stringify(latencyModeCache ?? {}));
  } catch {}
}

function getLatencyModePref(prefId: string): 'low' | 'smooth' | undefined {
  const val = loadLatencyModePrefs()[prefId];
  return val === 'smooth' || val === 'low' ? val : undefined;
}

function setLatencyModePref(prefId: string, mode: 'low' | 'smooth') {
  const prefs = loadLatencyModePrefs();
  prefs[prefId] = mode;
  saveLatencyModePrefs();
}

function purgeLatencyModePrefs(activeIds: Set<string>) {
  const prefs = loadLatencyModePrefs();
  let changed = false;
  for (const id of Object.keys(prefs)) {
    if (!activeIds.has(id)) {
      delete prefs[id];
      changed = true;
    }
  }
  if (changed) saveLatencyModePrefs();
}

// Mute e volume usam sessionStorage em vez de localStorage: persistem durante
// worker-refresh (sem reload da pagina) mas resetam no startup real.
const MUTED_KEY = 'stream-activity-muted';
let mutedCache: Record<string, boolean> | null = null;

function loadMutedPrefs(): Record<string, boolean> {
  if (mutedCache) return mutedCache;
  try {
    const raw = sessionStorage.getItem(MUTED_KEY);
    mutedCache = raw ? JSON.parse(raw) : {};
  } catch {
    mutedCache = {};
  }
  return mutedCache ?? {};
}

function saveMutedPrefs() {
  try {
    sessionStorage.setItem(MUTED_KEY, JSON.stringify(mutedCache ?? {}));
  } catch {}
}

function setMutedPref(prefId: string, muted: boolean | undefined) {
  const prefs = loadMutedPrefs();
  if (muted === undefined) {
    delete prefs[prefId];
  } else {
    prefs[prefId] = muted;
  }
  saveMutedPrefs();
}

function purgeMutedPrefs(activeIds: Set<string>) {
  const prefs = loadMutedPrefs();
  let changed = false;
  for (const id of Object.keys(prefs)) {
    if (!activeIds.has(id)) {
      delete prefs[id];
      changed = true;
    }
  }
  if (changed) saveMutedPrefs();
}

const VOLUME_KEY = 'stream-activity-volume';
let volumeCache: Record<string, number> | null = null;

function loadVolumePrefs(): Record<string, number> {
  if (volumeCache) return volumeCache;
  try {
    const raw = sessionStorage.getItem(VOLUME_KEY);
    volumeCache = raw ? JSON.parse(raw) : {};
  } catch {
    volumeCache = {};
  }
  return volumeCache ?? {};
}

function saveVolumePrefs() {
  try {
    sessionStorage.setItem(VOLUME_KEY, JSON.stringify(volumeCache ?? {}));
  } catch {}
}

function setVolumePref(prefId: string, volume: number | undefined) {
  const prefs = loadVolumePrefs();
  if (volume === undefined) {
    delete prefs[prefId];
  } else {
    prefs[prefId] = volume;
  }
  saveVolumePrefs();
}

function purgeVolumePrefs(activeIds: Set<string>) {
  const prefs = loadVolumePrefs();
  let changed = false;
  for (const id of Object.keys(prefs)) {
    if (!activeIds.has(id)) {
      delete prefs[id];
      changed = true;
    }
  }
  if (changed) saveVolumePrefs();
}

export interface Grid {
  element: HTMLElement;
  updateStreams: (streams: StreamInfo[]) => void;
  handleAudioFrame: (streamId: string, audioBuffer: ArrayBuffer, presentationTime: number, channels: number, sampleCount: number, sampleRate: number, timestamp: number) => void;
  handleVideoFrame: (streamId: string, frame: VideoFrame, presentationTime: number) => void;
  handleFirstFrame: (streamId: string) => void;
  handleMetrics: (streamId: string, metrics: StreamDecoderMetrics) => void;
  updateStreamState: (streamId: string, broadcasters: number, viewers: number) => void;
  setMyStreamId: (streamId: string | null) => void;
  setLatencyMode: (streamId: string, mode: 'low' | 'smooth') => void;
  getLatencyMode: (streamId: string) => 'low' | 'smooth';
  // Selecao travada (PiP do Discord): expande uma unica stream e bloqueia
  // troca/expansao manual ate ser liberada com null.
  setLockedSelection: (streamId: string | null) => void;
  isSelectionLocked: () => boolean;
  getPrimaryStreamId: () => string | null;
  hasExpanded: () => boolean;
  clear: () => void;
  // Reenvia as subscriptions watching pro websocket atual. Necessario apos
  // reconexao: o servidor so roteia pacotes pra viewers registrados em
  // stream.viewers, e um socket novo nasce sem nenhuma subscription.
  resubscribeAll: () => void;
}

let hasUserInteracted = false;
const interactionEvents = ['pointerdown', 'keydown', 'touchstart'];

function attachInteractionListener(callback: () => void) {
  const handler = () => {
    if (hasUserInteracted) return;
    hasUserInteracted = true;
    callback();
    for (const evt of interactionEvents) {
      document.removeEventListener(evt, handler);
    }
  };
  for (const evt of interactionEvents) {
    document.addEventListener(evt, handler, { passive: true });
  }
}

function resumeMediaElements() {
  for (const tile of document.querySelectorAll<HTMLVideoElement>('.tile-video')) {
    if (tile.paused) {
      tile.play().catch((e) => console.warn('[grid] auto play failed after interaction', e));
    }
  }
}

export function createGrid(
  root: HTMLElement,
  engine: MediaEngineHandle,
  onTileContextMenu?: (tile: Tile, x: number, y: number) => void
): Grid {
  const grid = document.createElement('div');
  grid.id = 'grid';
  grid.className = 'grid-mode';

  const empty = document.createElement('div');
  empty.id = 'empty';
  empty.innerHTML = `
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm-1 15h2v2h-2v-2Zm0-2h2V7h-2v8Z"/></svg>
    <div>Nenhuma transmissão ativa</div>
  `;

  root.appendChild(grid);
  root.appendChild(empty);

  const tiles = new Map<string, Tile>();
  const tileVideoStreams = new Map<string, MediaStreamTrackGenerator<VideoFrame>>();
  const tileAudioStreams = new Map<string, MediaStreamTrackGenerator<AudioData>>();
  const tileAudioBaseTimestamp = new Map<string, number>();
  const tileAudioLastTimestamp = new Map<string, number>();
  const tileCreatedAt = new Map<string, number>();
  const milkdropOverlays = new Map<string, MilkdropOverlay>();

  // Sincroniza o overlay MilkDrop do tile com o preset informado pelo
  // broadcaster (campo viz da stream). Cria lazy: precisa do tile watching e
  // do srcObject (audio) registrado. Chamado em updateStreams e em
  // registerMediaStream, que podem chegar em qualquer ordem.
  function syncMilkdrop(streamId: string) {
    const viz = streamInfos.find((s) => s.streamId === streamId)?.viz;
    const tile = tiles.get(streamId);
    if (!tile) return;

    if (viz && tile.watching) {
      const existing = milkdropOverlays.get(streamId);
      if (existing) {
        existing.setPreset(viz);
        return;
      }
      // Sem srcObject (registerMediaStream ainda nao rodou ou falhou) nao ha
      // audio a reagir: criar agora deixaria o overlay zumbi ate um notify
      // qualquer. registerMediaStream chama syncMilkdrop de novo ao registrar.
      if (!tile.video.srcObject) return;
      const hostElement = tile.element;
      const videoEl = tile.video;
      createMilkdropOverlay(
        hostElement,
        videoEl,
        viz,
        () => {
          const t = tiles.get(streamId);
          const last = tileAudioLastTimestamp.get(streamId);
          if (!t || last === undefined) return 0;
          // Fallback tap live: quanto de audio esta no buffer do elemento (ms).
          // Com o tap do elemento (captureStream) o lead nao se aplica.
          return Math.max(0, (last / 1e6 - t.video.currentTime) * 1000);
        }
      )
        .then((overlay) => {
          if (!overlay) return;
          // Tile removido e recriado com o mesmo streamId enquanto a criacao
          // estava pendente (reconexao rapida): o overlay ficaria ligado ao
          // elemento VELHO, fora do DOM, e o mapa impediria a recriacao.
          // Soh instala se o tile atual for o MESMO elemento capturado.
          const currentTile = tiles.get(streamId);
          if (!currentTile || currentTile.element !== hostElement || milkdropOverlays.has(streamId)) {
            overlay.destroy();
            return;
          }
          // O preset pode ter mudado enquanto a criacao estava pendente:
          // aplica o viz mais recente em vez do que disparou a criacao.
          const latestViz = streamInfos.find((s) => s.streamId === streamId)?.viz;
          if (latestViz) overlay.setPreset(latestViz);
          milkdropOverlays.set(streamId, overlay);
          console.log(`[grid ${streamId}] milkdrop overlay ativo`);
        })
        .catch((e) => console.warn(`[grid ${streamId}] milkdrop falhou`, e));
    } else if (milkdropOverlays.has(streamId)) {
      milkdropOverlays.get(streamId)!.destroy();
      milkdropOverlays.delete(streamId);
    }
  }

  // Tiles fora da viewport (scroll, PiP travado, janela minimizada) nao
  // precisam de decoder: destruir libera a sessao de decode hw pra quem esta
  // visivel. Audio continua roando independentemente. A visibilidade default
  // e true ate o primeiro callback do observer (que dispara imediatamente).
  const tileVisibility = new Map<string, boolean>();

  function registerMediaStream(streamId: string, tile: Tile) {
    if (tileVideoStreams.has(streamId)) return;
    if (typeof MediaStreamTrackGenerator === 'undefined') {
      console.warn(`[grid ${streamId}] MediaStreamTrackGenerator nao suportado neste navegador`);
      return;
    }
    try {
      const videoGenerator = new MediaStreamTrackGenerator<VideoFrame>({ kind: 'video' });
      // A audio track NAO entra aqui de proposito: soh entra no MediaStream do
      // tile quando o primeiro chunk de audio chegar (lazy em
      // handleAudioFrame). Stream publicada sem audio deixava a audio track
      // vazia no <video> e o elemento segurava a exibicao esperando audio
      // (loading eterno / stalls de sync). O WHEP nativo (só o que existe)
      // nao sofre disso.
      tile.video.srcObject = new MediaStream([videoGenerator]);
      tileVideoStreams.set(streamId, videoGenerator);
      const prefId = publicIds.get(streamId) || streamId;
      tile.video.muted = mutedStates.get(prefId) ?? true;
      engine.registerVideoStream(streamId);
      tile.video.play().catch((e) => console.warn(`[grid ${streamId}] video.play() falhou`, e));
      syncMilkdrop(streamId);
    } catch (e) {
      console.error(`[grid ${streamId}] falha ao registrar media stream`, e);
    }
  }

  function unregisterMediaStream(streamId: string) {
    const videoGenerator = tileVideoStreams.get(streamId);
    if (videoGenerator) {
      try {
        videoGenerator.stop?.();
      } catch {
        /* ignore */
      }
      tileVideoStreams.delete(streamId);
    }
    const audioGenerator = tileAudioStreams.get(streamId);
    if (audioGenerator) {
      try {
        audioGenerator.stop?.();
      } catch {
        /* ignore */
      }
      tileAudioStreams.delete(streamId);
    }
    tileAudioBaseTimestamp.delete(streamId);
    tileAudioLastTimestamp.delete(streamId);
    const overlay = milkdropOverlays.get(streamId);
    if (overlay) {
      overlay.destroy();
      milkdropOverlays.delete(streamId);
    }
    const tile = tiles.get(streamId);
    if (tile) {
      try {
        tile.video.pause();
        tile.video.srcObject = null;
      } catch {
        /* ignore */
      }
    }
    try {
      engine.unregisterVideoStream(streamId);
    } catch {
      /* ignore */
    }
  }

  function pauseVideoDecoder(streamId: string) {
    // Pausa apenas o decode de video no worker, mantendo a subscrição de
    // audio ativa no servidor. Isso permite que o áudio continue tocando
    // quando o tile sai da viewport ou a aba é minimizada.
    try {
      engine.setVideoPaused(streamId, true);
    } catch {
      /* ignore */
    }
  }

  function resumeVideoDecoder(streamId: string) {
    // Mostra loading ate o proximo keyframe ser decodificado e o primeiro
    // frame valido chegar, evitando artefatos de deltas sobre frame antigo.
    tiles.get(streamId)?.setSpinner(true);
    try {
      engine.setVideoPaused(streamId, false);
    } catch {
      /* ignore */
    }
  }

  const tileObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const streamId = (entry.target as HTMLElement).dataset.streamId;
        if (!streamId) continue;
        const wasVisible = tileVisibility.get(streamId) !== false;
        tileVisibility.set(streamId, entry.isIntersecting);
        if (wasVisible && !entry.isIntersecting) {
          pauseVideoDecoder(streamId);
        } else if (!wasVisible && entry.isIntersecting) {
          // Volta a aparecer: reativa decode de video e pede snapshot (ultimo
          // keyframe) pra nao esperar o GOP inteiro ate a imagem voltar. Audio
          // permaneceu ativo o tempo todo.
          const tile = tiles.get(streamId);
          if (tile?.watching) {
            resumeVideoDecoder(streamId);
          }
          try { engine.requestSnapshot(streamId); } catch { /* ignore */ }
        }
      }
    },
    { threshold: 0.05 }
  );

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      for (const streamId of tiles.keys()) pauseVideoDecoder(streamId);
    } else {
      for (const [streamId, tile] of tiles) {
        if (tile.watching && isTileVisible(streamId)) {
          resumeVideoDecoder(streamId);
          try { engine.requestSnapshot(streamId); } catch { /* ignore */ }
        }
      }
    }
  });

  function isTileVisible(streamId: string): boolean {
    if (document.hidden) return false;
    return tileVisibility.get(streamId) !== false;
  }

  // Hook de telemetria para testes automatizados. Com audio e video no mesmo
  // MediaStream, o navegador gerencia sync; mantemos o hook vazio para nao
  // quebrar ferramentas que o consultam.
  function getStreamSyncStats(_streamId: string) {
    return { offsetMs: null as number | null, audio: null, videoNextPresentationTime: null as number | null };
  }
  (window as unknown as { __avSync?: unknown }).__avSync = {
    getStats: getStreamSyncStats,
    getAllStats() {
      return {};
    },
  };
  const latencyModes = new Map<string, 'low' | 'smooth'>();
  const mutedStates = new Map<string, boolean>(Object.entries(loadMutedPrefs()));
  const volumeStates = new Map<string, number>(Object.entries(loadVolumePrefs()));
  const metricsHistory = new Map<string, Array<{ fps: number; bitrate: number; latency?: number; dropped: number; pacingStdDev?: number }>>();
  const latestMetrics = new Map<string, StreamDecoderMetrics>();
  const publicIds = new Map<string, string>();
  const MAX_HISTORY = 120;
  const MAX_PACING_SAMPLES = 120;
  const framePacingDeltas = new Map<string, number[]>();
  const lastFrameDeliveryTime = new Map<string, number>();
  let myStreamId: string | null = null;
  let lockedStreamId: string | null = null;
  let streamInfos: StreamInfo[] = [];
  attachInteractionListener(() => {
    resumeMediaElements();
  });

  function computePacing(streamId: string): FramePacingMetrics | undefined {
    const deltas = framePacingDeltas.get(streamId);
    if (!deltas || deltas.length < 2) return undefined;
    const sum = deltas.reduce((a, b) => a + b, 0);
    const avg = sum / deltas.length;
    const min = Math.min(...deltas);
    const max = Math.max(...deltas);
    const variance = deltas.reduce((acc, d) => acc + (d - avg) ** 2, 0) / deltas.length;
    return { avgDeltaMs: avg, stdDevMs: Math.sqrt(variance), minDeltaMs: min, maxDeltaMs: max };
  }

  function recordFrameDelivery(streamId: string) {
    const now = performance.now();
    const last = lastFrameDeliveryTime.get(streamId);
    if (last !== undefined) {
      const deltas = framePacingDeltas.get(streamId) || [];
      deltas.push(now - last);
      if (deltas.length > MAX_PACING_SAMPLES) deltas.shift();
      framePacingDeltas.set(streamId, deltas);
    }
    lastFrameDeliveryTime.set(streamId, now);
  }

  function handleMetrics(streamId: string, metrics: StreamDecoderMetrics) {
    const tile = tiles.get(streamId);
    if (!tile) return;
    // Pacing agora eh medido na main thread pela cadencia de entrega dos
    // frames ao <video>. Sobrescreve o pacing vazio do worker.
    metrics.pacing = computePacing(streamId);
    tile.setMetrics(metrics as any);
    latestMetrics.set(streamId, metrics);
    const history = metricsHistory.get(streamId) || [];
    history.push({
      fps: metrics.fps,
      bitrate: metrics.bitrate,
      latency: metrics.latency,
      dropped: metrics.droppedFrames,
      pacingStdDev: metrics.pacing?.stdDevMs,
    });
    if (history.length > MAX_HISTORY) history.shift();
    metricsHistory.set(streamId, history);
    tile.updateChart(history);
    if (metrics.pacing && diagLogsEnabled()) {
      const pacingKey = `pacing-log-${streamId}`;
      const lastPacingLog = (window as unknown as Record<string, number>)[pacingKey] || 0;
      if (Date.now() - lastPacingLog >= 1000) {
        (window as unknown as Record<string, number>)[pacingKey] = Date.now();
        console.log(
          `[grid ${streamId}] pacing: avg=${metrics.pacing.avgDeltaMs.toFixed(1)}ms std=${metrics.pacing.stdDevMs.toFixed(1)}ms min=${metrics.pacing.minDeltaMs.toFixed(0)} max=${metrics.pacing.maxDeltaMs.toFixed(0)}`
        );
      }
    }
  }

  function handleFirstFrame(streamId: string) {
    const tile = tiles.get(streamId);
    if (tile) {
      tile.setSpinner(false);
      // Restaura estado de mute salvo (util apos worker-refresh sem reload).
      const prefId = publicIds.get(streamId) || streamId;
      const muted = mutedStates.get(prefId);
      if (muted !== undefined && tile.video.muted !== muted) {
        tile.video.muted = muted;
        tile.setMuted(muted);
      }
    }
    const created = tileCreatedAt.get(streamId);
    if (created !== undefined) {
      console.log(`[grid ${streamId}] primeiro frame recebido apos ${(performance.now() - created).toFixed(1)}ms do tile ser criado`);
    }
  }

  function updateEmpty() {
    const hasTiles = tiles.size > 0;
    grid.style.display = hasTiles ? 'grid' : 'none';
    empty.style.display = hasTiles ? 'none' : 'flex';
  }

  function recalcLayout() {
    const n = tiles.size;
    if (n === 0) return;

    const hasExpanded = Array.from(tiles.values()).some((t) => t.expanded);
    if (hasExpanded) {
      grid.style.gridTemplateColumns = '';
      return;
    }

    const rect = grid.getBoundingClientRect();
    const style = getComputedStyle(grid);
    const padX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const padY = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const gap = parseFloat(style.gap) || 12;

    const availableW = Math.max(1, rect.width - padX);
    const availableH = Math.max(1, rect.height - padY);

    let bestC = 1;
    let bestW = 0;
    let bestScore = -1;
    let fallbackC = 1;
    let fallbackH = Infinity;

    for (let c = 1; c <= n; c++) {
      const rows = Math.ceil(n / c);
      const tileWFromWidth = (availableW - (c - 1) * gap) / c;
      const tileHMax = (availableH - (rows - 1) * gap) / rows;
      const tileWFromHeight = tileHMax * (16 / 9);
      const tileW = Math.max(1, Math.min(tileWFromWidth, tileWFromHeight));
      const tileH = tileW * (9 / 16);
      const totalH = rows * tileH + (rows - 1) * gap;
      const score = tileW * tileH;

      if (totalH <= availableH && score > bestScore) {
        bestC = c;
        bestW = tileW;
        bestScore = score;
      }
      if (totalH < fallbackH) {
        fallbackH = totalH;
        fallbackC = c;
      }
    }

    if (bestScore < 0) {
      bestC = fallbackC;
      const rows = Math.ceil(n / bestC);
      const tileWFromWidth = (availableW - (bestC - 1) * gap) / bestC;
      const tileHMax = (availableH - (rows - 1) * gap) / rows;
      bestW = Math.max(1, Math.min(tileWFromWidth, tileHMax * (16 / 9)));
    }

    grid.style.gridTemplateColumns = `repeat(${bestC}, minmax(0, ${bestW}px))`;
  }

  const layoutObserver = new ResizeObserver(() => recalcLayout());
  layoutObserver.observe(grid);

  function collapseOthers(exceptStreamId: string) {
    for (const [streamId, tile] of tiles) {
      if (streamId !== exceptStreamId) {
        tile.setExpanded(false);
      }
    }
  }

  function setLockedSelection(streamId: string | null) {
    if (lockedStreamId === streamId) return;
    const prev = lockedStreamId ? tiles.get(lockedStreamId) : null;
    if (prev) {
      prev.selectionLocked = false;
      if (prev.expanded) prev.setExpanded(false);
    }
    lockedStreamId = null;
    if (!streamId) return;
    const tile = tiles.get(streamId);
    if (!tile || !tile.watching) return;
    tile.selectionLocked = true;
    lockedStreamId = streamId;
    tile.setExpanded(true);
  }

  function isSelectionLocked(): boolean {
    return lockedStreamId !== null;
  }

  function getPrimaryStreamId(): string | null {
    const first = tiles.keys().next();
    return first.done ? null : (first.value as string);
  }

  function hasExpanded(): boolean {
    return Array.from(tiles.values()).some((t) => t.expanded);
  }

  function ensureTile(streamId: string, publicId?: string): Tile {
    if (publicId) {
      publicIds.set(streamId, publicId);
    }
    const prefId = publicIds.get(streamId) || streamId;

    if (tiles.has(streamId)) {
      return tiles.get(streamId)!;
    }

    const tile = createTile(streamId, `Stream ${streamId.slice(0, 4)}`, streamId === myStreamId, (x, y) => {
      onTileContextMenu?.(tile, x, y);
    });
    tile.element.dataset.streamId = streamId;
    tileObserver.observe(tile.element);

    const savedShowId = getShowIdPref(prefId);
    if (savedShowId !== undefined) {
      tile.setShowId(savedShowId);
    }

    const savedMuted = mutedStates.get(prefId);
    const initialMuted = savedMuted === undefined ? true : savedMuted;
    tile.setMuted(initialMuted);
    tile.video.muted = initialMuted;
    mutedStates.set(prefId, initialMuted);
    setMutedPref(prefId, initialMuted);

    const savedVolume = volumeStates.get(prefId);
    const initialVolume = savedVolume ?? 1;
    volumeStates.set(prefId, initialVolume);
    setVolumePref(prefId, initialVolume);

    tile.onShowIdToggle = () => {
      const show = !tile.showId;
      tile.setShowId(show);
      setShowIdPref(prefId, show);
    };

    tile.onWatch = () => {
      if (tile.watching) return;
      tile.setWatching(true);
      try { engine.setWatching(streamId, true); } catch { /* ignore */ }
      setWatchingPref(prefId, true);
      registerMediaStream(streamId, tile);
      try { engine.requestSnapshot(streamId); } catch { /* ignore */ }
    };

    tile.onMuteToggle = () => {
      const currentPrefId = publicIds.get(streamId) || streamId;
      const muted = !mutedStates.get(currentPrefId);
      mutedStates.set(currentPrefId, muted);
      setMutedPref(currentPrefId, muted);
      tile.setMuted(muted);
      tile.video.muted = muted;
      if (!muted) {
        tile.video.play().catch((e) => console.warn(`[grid ${streamId}] video.play() falhou ao desmutar`, e));
      }
    };

    tile.onVolumeChange = (volume) => {
      const currentPrefId = publicIds.get(streamId) || streamId;
      const clamped = Math.max(0, Math.min(1, volume));
      tile.volume = clamped;
      volumeStates.set(currentPrefId, clamped);
      setVolumePref(currentPrefId, clamped);
      tile.video.volume = clamped;
    };

    if (!tile.isMine && getWatchingPref(prefId) && !tile.watching) {
      tile.onWatch();
    }

    tile.onWatchStop = () => {
      stopWatching(streamId);
    };

    tile.element.addEventListener('transitionend', () => {
      if (!tile.expanded) {
        tile.element.style.zIndex = '';
      }
    });

    tile.setExpanded = (expanded) => {
      tile.expanded = expanded;
      tile.element.classList.toggle('expanded', expanded);
      if (expanded) {
        collapseOthers(streamId);
        tile.element.style.zIndex = '100';
      }
      recalcLayout();
    };

    grid.appendChild(tile.element);
    tiles.set(streamId, tile);
    tileCreatedAt.set(streamId, performance.now());
    // Informa o worker sobre o estado inicial de watching do tile.
    try { engine.setWatching(streamId, tile.watching); } catch { /* ignore */ }
    updateEmpty();
    return tile;
  }

  function stopWatching(streamId: string) {
    const prefId = publicIds.get(streamId) || streamId;
    const tile = tiles.get(streamId);
    if (tile && tile.watching) {
      tile.setWatching(false);
      try { engine.setWatching(streamId, false); } catch { /* ignore */ }
      setWatchingPref(prefId, false);
    }
    unregisterMediaStream(streamId);
  }

  function removeTile(streamId: string) {
    const tile = tiles.get(streamId);
    if (tile) {
      stopWatching(streamId);
      tileObserver.unobserve(tile.element);
      tile.destroy();
      tiles.delete(streamId);
    }
    tileVisibility.delete(streamId);
    metricsHistory.delete(streamId);
    latestMetrics.delete(streamId);
    publicIds.delete(streamId);
    framePacingDeltas.delete(streamId);
    lastFrameDeliveryTime.delete(streamId);
    updateEmpty();
  }

  function updateStreams(streams: StreamInfo[]) {
    streamInfos = streams;
    const activeIds = new Set(streams.map((s) => s.streamId));
    const activePublicIds = new Set(streams.map((s) => s.publicId).filter(Boolean) as string[]);
    purgeShowIdPrefs(activePublicIds);
    purgeWatchingPrefs(activePublicIds);
    purgeLatencyModePrefs(activePublicIds);
    purgeMutedPrefs(activePublicIds);
    purgeVolumePrefs(activePublicIds);

    for (const stream of streams) {
      // Se o tile foi criado antes de conhecermos o publicId (ex: via pacote de video/audio),
      // migra o estado de audio temporario do streamId para o publicId permanente.
      if (stream.publicId) {
        publicIds.set(stream.streamId, stream.publicId);
        if (mutedStates.has(stream.streamId) && !mutedStates.has(stream.publicId)) {
          // Migra estado temporario do streamId para o publicId permanente,
          // sem sobrescrever um valor ja salvo no publicId.
          const value = mutedStates.get(stream.streamId)!;
          mutedStates.set(stream.publicId, value);
          setMutedPref(stream.publicId, value);
          mutedStates.delete(stream.streamId);
          setMutedPref(stream.streamId, undefined);
        }
        if (volumeStates.has(stream.streamId) && !volumeStates.has(stream.publicId)) {
          const value = volumeStates.get(stream.streamId)!;
          volumeStates.set(stream.publicId, value);
          setVolumePref(stream.publicId, value);
          volumeStates.delete(stream.streamId);
          setVolumePref(stream.streamId, undefined);
        }
      }

      // O viewer controla o modo de latencia. Usa a preferencia salva se existir;
      // caso contrario, usa o modo informado pelo broadcaster como default inicial.
      const prefId = stream.publicId || stream.streamId;
      const broadcasterMode = stream.latencyMode === 'smooth' ? 'smooth' : 'low';
      const mode = getLatencyModePref(prefId) ?? broadcasterMode;
      latencyModes.set(stream.streamId, mode);
      const tile = ensureTile(stream.streamId, stream.publicId);
      tile.setViewerCount(stream.viewers);
      // Sincroniza estado de audio do tile com o estado salvo (util apos
      // migração de streamId para publicId ou worker-refresh).
      const savedMuted = mutedStates.get(prefId);
      if (savedMuted !== undefined && tile.video.muted !== savedMuted) {
        tile.video.muted = savedMuted;
        tile.setMuted(savedMuted);
      }
      const savedVolume = volumeStates.get(prefId);
      if (savedVolume !== undefined && tile.video.volume !== savedVolume) {
        tile.video.volume = savedVolume;
        tile.volume = savedVolume;
      }
      syncMilkdrop(stream.streamId);
    }

    for (const [streamId] of tiles) {
      if (!activeIds.has(streamId)) {
        removeTile(streamId);
      }
    }

    if (lockedStreamId && !tiles.has(lockedStreamId)) {
      lockedStreamId = null;
    }

    updateEmpty();
    recalcLayout();
  }

  let dbgMainVideoWritten = 0;
  let dbgMainAudioWritten = 0;

  async function handleVideoFrame(streamId: string, frame: VideoFrame, _presentationTime: number) {
    if (streamId === myStreamId) return;
    const tile = tiles.get(streamId);
    if (!tile || !tile.watching) {
      frame.close();
      return;
    }
    if (!isTileVisible(streamId)) {
      frame.close();
      return;
    }

    if (!tileVideoStreams.has(streamId)) {
      registerMediaStream(streamId, tile);
      frame.close();
      return;
    }

    const generator = tileVideoStreams.get(streamId);
    if (!generator) {
      frame.close();
      return;
    }

    const writer = generator.writable.getWriter();
    try {
      await writer.write(frame);
      dbgMainVideoWritten++;
      recordFrameDelivery(streamId);
    } catch (e) {
      console.warn(`[grid ${streamId}] falha ao escrever frame no video track`, e);
    } finally {
      writer.releaseLock();
      try { frame.close(); } catch { /* ignore */ }
    }
  }

  async function handleAudioFrame(
    streamId: string,
    audioBuffer: ArrayBuffer,
    _presentationTime: number,
    channels: number,
    sampleCount: number,
    sampleRate: number,
    timestamp: number
  ) {
    if (streamId === myStreamId) return;
    const tile = tiles.get(streamId);
    if (!tile || !tile.watching) return;

    let generator = tileAudioStreams.get(streamId);
    if (!generator) {
      // Lazy: a stream pode comecar sem audio (ou nunca ter). A audio track
      // soh entra no <video> quando o primeiro chunk chega; com a track vazia
      // o elemento segurava a exibicao esperando audio.
      if (typeof MediaStreamTrackGenerator === 'undefined') return;
      try {
        const stream = tile.video.srcObject as MediaStream | null;
        if (!stream) return;
        generator = new MediaStreamTrackGenerator<AudioData>({ kind: 'audio' });
        stream.addTrack(generator);
        tileAudioStreams.set(streamId, generator);
      } catch (e) {
        console.warn(`[grid ${streamId}] falha ao adicionar audio track ao tile`, e);
        return;
      }
    }

    // O worker envia o ArrayBuffer planar; reconstruimos a view e o AudioData
    // na main thread para evitar transferir o objeto WebCodecs entre contexts.
    const planar = new Float32Array(audioBuffer, 0, sampleCount * channels);
    if (sampleCount === 0 || planar.length === 0) {
      console.warn(`[grid ${streamId}] audio frame vazio`);
      return;
    }

    // MediaStreamTrackGenerator de audio espera timestamps monotonicos
    // relativos ao inicio da track. Normaliza usando o primeiro timestamp
    // recebido como base.
    let base = tileAudioBaseTimestamp.get(streamId);
    if (base === undefined) {
      base = timestamp;
      tileAudioBaseTimestamp.set(streamId, base);
    }
    const relativeTimestamp = Math.max(0, timestamp - base);
    tileAudioLastTimestamp.set(streamId, relativeTimestamp);

    const audioData = new AudioData({
      format: 'f32-planar',
      sampleRate,
      numberOfFrames: sampleCount,
      numberOfChannels: channels,
      timestamp: relativeTimestamp,
      data: planar as unknown as ArrayBuffer,
    });

    const writer = generator.writable.getWriter();
    try {
      await writer.write(audioData);
      dbgMainAudioWritten++;
    } catch (e) {
      console.warn(`[grid ${streamId}] falha ao escrever audioData no audio track`, e);
    } finally {
      writer.releaseLock();
      try { audioData.close(); } catch { /* ignore */ }
    }
  }

  function updateStreamState(streamId: string, _broadcasters: number, viewers: number) {
    const tile = tiles.get(streamId);
    if (tile) {
      tile.setViewerCount(viewers);
    }
  }

  function setMyStreamId(streamId: string | null) {
    myStreamId = streamId;
    updateStreams(streamInfos);
  }

  function getLatencyMode(streamId: string): 'low' | 'smooth' {
    return latencyModes.get(streamId) || 'low';
  }

  function setLatencyMode(streamId: string, mode: 'low' | 'smooth') {
    const prefId = publicIds.get(streamId) || streamId;
    const current = latencyModes.get(streamId);
    if (current === mode) return;
    latencyModes.set(streamId, mode);
    setLatencyModePref(prefId, mode);

    try { engine.setLatencyMode(streamId, mode); } catch { /* ignore */ }

    // O worker recria o decoder automaticamente quando o modo muda. A
    // renderizacao por <video> na main thread nao depende do modo de latencia.
    const tile = tiles.get(streamId);
    if (tile?.watching) {
      console.log(`[grid] latencyMode alterado pelo viewer para ${mode}`);
    }
  }

  function clear() {
    for (const [streamId] of tiles) {
      removeTile(streamId);
    }
    publicIds.clear();
    tileVisibility.clear();
    tileObserver.disconnect();
    layoutObserver.disconnect();
  }

  updateEmpty();

  (window as any).__viewerPerf = {
    getAll: () => {
      const out: Record<string, StreamDecoderMetrics> = {};
      for (const [id, m] of latestMetrics) {
        out[id] = m;
      }
      return out;
    },
    getMainWriteStats: () => ({ video: dbgMainVideoWritten, audio: dbgMainAudioWritten }),
    getRenderer: (streamId: string) => {
      const m = latestMetrics.get(streamId);
      if (!m) return undefined;
      return {
        queueSize: 0,
        framesReceived: m.decodedFrames,
        framesRendered: m.decodedFrames - m.droppedFrames,
        framesDroppedQueue: 0,
        renderFps: m.fps,
        avgRenderTimeMs: 0,
        maxRenderTimeMs: 0,
        pacing: m.pacing,
      };
    },
    // Playback na camada de exibicao (<video>): stalls sao gaps >300ms entre
    // frames apresentados. Mesma metrica do test-whep.html (WHEP nativo).
    getPlayback: (streamId: string) => {
      const t = tiles.get(streamId);
      if (!t) return undefined;
      const q = (() => {
        try {
          return t.video.getVideoPlaybackQuality();
        } catch {
          return null;
        }
      })();
      return {
        ...t.playback,
        droppedVideoFrames: q?.droppedVideoFrames ?? 0,
        totalVideoFrames: q?.totalVideoFrames ?? 0,
        readyState: t.video.readyState,
      };
    },
  };

  return {
    element: grid,
    updateStreams,
    handleAudioFrame,
    handleVideoFrame,
    handleFirstFrame,
    handleMetrics,
    updateStreamState,
    setMyStreamId,
    setLatencyMode,
    getLatencyMode,
    setLockedSelection,
    isSelectionLocked,
    getPrimaryStreamId,
    hasExpanded,
    clear,
    resubscribeAll() {
      for (const [streamId, tile] of tiles) {
        if (!tile.watching) continue;
        try { engine.setWatching(streamId, true); } catch { /* ignore */ }
        if (isTileVisible(streamId)) {
          try { engine.requestSnapshot(streamId); } catch { /* ignore */ }
        }
      }
    },
  };
}
